import {
  ChangeDetectionStrategy,
  Component,
  computed,
  Inject,
  OnInit,
  signal,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';
import {toSignal} from '@angular/core/rxjs-interop';
import {CdkVirtualScrollViewport} from '@angular/cdk/scrolling';
import {FormControl} from '@angular/forms';
import {MatDialogRef, MAT_DIALOG_DATA} from '@angular/material/dialog';
import {differenceBy} from 'lodash';
import {debounceTime} from 'rxjs/operators';
import {ConfigService, TakeUntilComponentDestroy} from '@q9elements/ui-kit/common';
import {ResourcesDaoService} from './services/resources-dao.service';
import {ResourceListItem} from './models/resource-list-item.interface';
import {FilterResourceListPipe} from './pipes/filter-resource-list.pipe';
import {ResourceSystemTypes, ResourceTypes} from 'src/app/shared/constants/resource-types';
import {AddResourceDialogModes, AddResourceDialogPayload} from './models/add-resource-dialog';

interface ResourceVM {
  isMain: boolean;
  mainResource: ResourceListItem | null;
}

@Component({
  selector: 'add-resource-dialog',
  templateUrl: './add-resource-dialog.component.html',
  styleUrls: ['./add-resource-dialog.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AddResourceDialogComponent extends TakeUntilComponentDestroy implements OnInit {
  /** Fixed virtual-scroll row height. MUST stay in sync with `.resource-option` in the SCSS. */
  public readonly ROW_HEIGHT = 44;

  public attachHidden = false;
  public mode = signal(AddResourceDialogModes.LOADING);
  public error = signal('');
  public all = signal<ResourceListItem[]>([]);
  public selected = signal<ResourceListItem[]>([]);
  private isMain = signal(false);
  public activeIndex = signal(-1);

  @ViewChild(CdkVirtualScrollViewport) private viewport?: CdkVirtualScrollViewport;

  public nameFilterFormControl = new FormControl('', {nonNullable: true});
  public filterTypeFormControl = new FormControl('All', {nonNullable: true});
  public filterSystemFormControl = new FormControl('All', {nonNullable: true});

  private name = toSignal(this.nameFilterFormControl.valueChanges.pipe(debounceTime(400)), {
    initialValue: this.nameFilterFormControl.value
  });
  private type = toSignal(this.filterTypeFormControl.valueChanges, {
    initialValue: this.filterTypeFormControl.value
  });
  private system = toSignal(this.filterSystemFormControl.valueChanges, {
    initialValue: this.filterSystemFormControl.value
  });

  public resourceVM = computed<ResourceVM>(() => {
    const selected = this.selected();
    const isMain = this.isMain();

    return {
      isMain,
      mainResource: (isMain && selected.length === 1 && selected[0]) || null
    };
  });
  public filteredResources = computed(
    () =>
      this.filterListPipe.transform(
        this.all(),
        this.name(),
        this.type(),
        this.system()
      ) as ResourceListItem[]
  );
  public activeId = computed(() => {
    const i = this.activeIndex();

    return i >= 0 && i < this.filteredResources().length ? this.optionId(i) : null;
  });

  public resourcesTypes: string[];
  public systems: string[];
  public isArchitectural: boolean;
  public noResourcesImage = `${ConfigService.environment.STATIC_FILES_URL}/icons/empty-filter.svg`;
  public dialogModes = AddResourceDialogModes;

  constructor(
    protected dialogRef: MatDialogRef<AddResourceDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: AddResourceDialogPayload,
    private filterListPipe: FilterResourceListPipe,
    private resourceService: ResourcesDaoService
  ) {
    super();
  }

  ngOnInit() {
    this.initComponentData();
    this.fetchResources();
  }

  public isSelected(res: ResourceListItem): boolean {
    return this.selected().includes(res);
  }

  public trackByRef = (_: number, res: ResourceListItem): ResourceListItem => res;

  public isOptionDisabled(res: ResourceListItem, vm: ResourceVM | null): boolean {
    if (!vm?.isMain || this.selected().length === 0) {
      return false;
    }

    return vm.mainResource?.id !== res.id;
  }

  public changeHiddenFlag(hidden: boolean) {
    this.attachHidden = hidden;
  }

  public toggleResource(res: ResourceListItem): void {
    this.error.set('');
    this.selected.update(list =>
      list.includes(res) ? list.filter(r => r !== res) : [...list, res]
    );
  }

  public optionId(index: number): string {
    return `add-resource-option-${index}`;
  }

  public onOptionClick(index: number, res: ResourceListItem): void {
    if (this.isOptionDisabled(res, this.resourceVM())) {
      return;
    }

    this.activeIndex.set(index);
    this.toggleResource(res);
  }

  public onListFocus(list: ResourceListItem[]): void {
    const i = this.activeIndex();
    const valid = i >= 0 && i < list.length && !this.isOptionDisabled(list[i], this.resourceVM());

    if (valid) {
      return;
    }

    const firstVisible = this.viewport
      ? Math.floor(this.viewport.measureScrollOffset() / this.ROW_HEIGHT)
      : 0;
    const next = this.nextSelectable(list, firstVisible, 1);

    if (next !== null) {
      this.activeIndex.set(next);
    }
  }

  public onListBlur(): void {
    this.activeIndex.set(-1);
  }

  public onListKeydown(event: KeyboardEvent, list: ResourceListItem[]): void {
    const key = event.key;

    if (key === 'Enter' || key === ' ') {
      event.preventDefault();

      const res = list[this.activeIndex()];

      if (res && !this.isOptionDisabled(res, this.resourceVM())) {
        this.toggleResource(res);
      }

      return;
    }

    let target: number | null = null;

    if (key === 'ArrowDown') {
      target = this.nextSelectable(list, this.activeIndex() + 1, 1);
    } else if (key === 'ArrowUp') {
      target = this.nextSelectable(list, this.activeIndex() - 1, -1);
    } else if (key === 'Home') {
      target = this.nextSelectable(list, 0, 1);
    } else if (key === 'End') {
      target = this.nextSelectable(list, list.length - 1, -1);
    }

    if (target === null) {
      return;
    }

    event.preventDefault();
    this.setActive(target);
  }

  public addResource(): void {
    const selected = this.selected();

    if (!selected.length) {
      this.error.set('CORE.RESOURCES.SELECT_RESOURCE');

      return;
    }

    if (this.attachHidden) {
      selected.forEach(res => {
        res.hidden = true;
      });
    }

    if (this.isMain()) {
      selected[0].main = true;
    }

    this.dialogRef.close({successful: true, data: selected});
  }

  public choosingMainToggle(isMain: boolean): void {
    if (this.selected().length <= 1) {
      this.isMain.set(isMain);
    }
  }

  private initComponentData() {
    this.isArchitectural = this.data.isArchitecture;

    this.resourcesTypes = ['All', ...Object.keys(ResourceTypes)];
    this.systems = ['All', ...Object.keys(ResourceSystemTypes)];
  }

  private fetchResources() {
    const fetchUrl = `${ConfigService.environment.API_ENDPOINT}/organization/${this.data.teamId}/resource`;

    this.resourceService
      .list(fetchUrl)
      .pipe(this.takeUntilComponentDestroy())
      .subscribe((res: ResourceListItem[]) => {
        const resources = differenceBy(res, this.data.attachedResources, 'id').sort((a, b) =>
          a.name.localeCompare(b.name)
        );

        this.all.set(resources);
        this.mode.set(
          resources.length ? AddResourceDialogModes.ADD : AddResourceDialogModes.CREATE
        );
      });
  }

  private nextSelectable(list: ResourceListItem[], start: number, step: 1 | -1): number | null {
    const vm = this.resourceVM();

    for (let i = start; i >= 0 && i < list.length; i += step) {
      if (!this.isOptionDisabled(list[i], vm)) {
        return i;
      }
    }

    return null;
  }

  private setActive(index: number): void {
    this.activeIndex.set(index);
    this.ensureVisible(index);
  }

  private ensureVisible(index: number): void {
    const viewport = this.viewport;

    if (!viewport) {
      return;
    }

    const rowTop = index * this.ROW_HEIGHT;
    const rowBottom = rowTop + this.ROW_HEIGHT;
    const scrollTop = viewport.measureScrollOffset();
    const viewportSize = viewport.getViewportSize();

    if (rowTop < scrollTop) {
      viewport.scrollToOffset(rowTop);
    } else if (rowBottom > scrollTop + viewportSize) {
      viewport.scrollToOffset(rowBottom - viewportSize);
    }
  }
}
